import { Notifier, Option, panic, RuntimeNotifier, Terminable, Terminator } from "@opendaw/lib-std";
import { AudioData } from "@opendaw/lib-dsp";
import { Communicator, Messenger, Promises } from "@opendaw/lib-runtime";
import { mergeChunkPlanes, RecordingProcessorChannel, RingBuffer } from "@opendaw/studio-adapters";
import { RenderQuantum } from "./RenderQuantum";
import { PeaksWriter } from "./PeaksWriter";
import { SampleStorage } from "./samples";
// the ring delivers whole chunks, so the capture overshoots the limit: keep the head, drop the tail
export const recordedFrames = (chunks, numFrames) => mergeChunkPlanes(chunks.slice(0, Math.ceil(numFrames / RenderQuantum)), RenderQuantum, numFrames);
export class RecordingWorklet extends AudioWorkletNode {
    #terminator = new Terminator();
    uuid;
    #output;
    #notifier;
    #reader;
    #peakWriter;
    #data = Option.None;
    #peaks = Option.None;
    #meta = Option.None;
    #firstQuantumTime = Option.None;
    #isRecording = true;
    #limitSamples = Number.POSITIVE_INFINITY;
    #state = { type: "record" };
    #onSaved = Option.None;
    #sampleService = Option.None;
    #bpm = Option.None;
    constructor(context, uuid, config) {
        super(context, "recording-processor", {
            numberOfInputs: 1,
            channelCount: config.numberOfChannels,
            channelCountMode: "explicit",
            processorOptions: config
        });
        this.uuid = uuid;
        this.#peakWriter = new PeaksWriter(config.numberOfChannels);
        this.#peaks = Option.wrap(this.#peakWriter);
        this.#terminator.own(Communicator.executor(Messenger.for(this.port).channel(RecordingProcessorChannel), {
            firstQuantum: (contextTime) => { this.#firstQuantumTime = Option.wrap(contextTime); }
        }));
        this.#output = [];
        this.#notifier = new Notifier();
        this.#reader = RingBuffer.reader(config, array => {
            if (this.#isRecording) {
                this.#output.push(array);
                this.#peakWriter.append(array);
                if (this.numberOfFrames >= this.#limitSamples) {
                    void this.#finalize();
                }
            }
        });
    }
    own(terminable) { return this.#terminator.own(terminable); }
    limit(count) {
        if (!this.#isRecording) {
            return;
        }
        this.#limitSamples = count;
        if (this.numberOfFrames >= this.#limitSamples) {
            void this.#finalize();
        }
    }
    set onSaved(callback) { this.#onSaved = Option.wrap(callback); }
    set bpm(value) { this.#bpm = Option.wrap(value); }
    set sampleService(service) { this.#sampleService = Option.wrap(service); }
    setFillLength(value) { this.#peakWriter.numFrames = value; }
    get numberOfFrames() { return this.#output.length * RenderQuantum; }
    get firstQuantumTime() { return this.#firstQuantumTime; }
    get meta() { return this.#meta; }
    get data() { return this.#data; }
    get peaks() { return this.#peaks.isEmpty() ? Option.wrap(this.#peakWriter) : this.#peaks; }
    get state() { return this.#state; }
    invalidate() { }
    subscribe(observer) {
        if (this.#state.type === "loaded") {
            observer(this.#state);
            return Terminable.Empty;
        }
        return this.#notifier.subscribe(observer);
    }
    terminate() {
        this.#reader.stop();
        this.#isRecording = false;
        this.#terminator.terminate();
    }
    toString() { return `{RecordingWorklet}`; }
    async #finalize() {
        this.#isRecording = false;
        this.#reader.stop();
        if (this.#output.length === 0) {
            return panic("No recording data available");
        }
        const totalSamples = this.#limitSamples;
        const mergedFrames = recordedFrames(this.#output, totalSamples);
        const audioData = AudioData.create(this.context.sampleRate, totalSamples, this.channelCount);
        mergedFrames.forEach((frame, index) => audioData.frames[index].set(frame));
        this.#data = Option.wrap(audioData);
        const bpm = this.#bpm.unwrapOrElse(120);
        const imported = await Promises.tryCatch(this.#sampleService
            .unwrap("SampleService not set")
            .importRecording(this.uuid, audioData, bpm));
        if (imported.status === "resolved") {
            this.#peaks = Option.wrap(await SampleStorage.get().loadPeaks(this.uuid, audioData));
            this.#meta = Option.wrap(imported.value);
        }
        else {
            const { sampleRate } = this.context;
            this.#meta = Option.wrap({
                name: "Recording", bpm, duration: totalSamples / sampleRate, sample_rate: sampleRate, origin: "recording"
            });
            RuntimeNotifier.info({
                headline: "Storage Unavailable",
                message: `The recording could not be saved to local storage (${String(imported.error)}). It stays playable until you close the tab.`
            }).finally();
        }
        this.#output.length = 0;
        this.#onSaved.ifSome(callback => callback());
        this.#setState({ type: "loaded" });
        this.terminate();
    }
    #setState(value) {
        this.#state = value;
        this.#notifier.notify(this.#state);
    }
}
